"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@workspace/ui/lib/utils";
import { homeAssets } from "../../_utils/home-assets";

const CONFIRM_FRAMES = homeAssets.confirm.frames;
const FIRST_CONFIRM_FRAME = CONFIRM_FRAMES[0]!;

const FRAME_DELAY_MS = 3000;

type ConfirmVisualProps = {
  imageAlt: string;
};

export function ConfirmVisual({ imageAlt }: ConfirmVisualProps) {
  const [frameIndex, setFrameIndex] = useState(0);
  const prevFrameRef = useRef(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setFrameIndex((cur) => {
        prevFrameRef.current = cur;
        return (cur + 1) % CONFIRM_FRAMES.length;
      });
    }, FRAME_DELAY_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  const prevIndex = prevFrameRef.current;

  return (
    <div className="mx-auto w-full max-w-[382px] overflow-hidden rounded-2xl xl:max-w-none xl:rounded-[20px]">
      <div
        className="relative w-full"
        style={{
          aspectRatio: `${FIRST_CONFIRM_FRAME.width} / ${FIRST_CONFIRM_FRAME.height}`,
        }}
      >
        {CONFIRM_FRAMES.map((frame, index) => {
          const isActive = index === frameIndex;
          const isPrev = index === prevIndex && prevIndex !== frameIndex;
          return (
            <Image
              key={frame.src}
              src={frame.src}
              alt={isActive ? imageAlt : ""}
              aria-hidden={isActive ? undefined : true}
              fill
              sizes="(max-width: 1279px) min(100vw - 48px, 382px), 600px"
              className={cn(
                "object-contain transition-opacity duration-700 ease-in-out motion-reduce:transition-none motion-reduce:duration-0",
                isActive && "z-[3] opacity-100",
                isPrev && "z-[2] opacity-100",
                !isActive && !isPrev && "pointer-events-none z-[1] opacity-0",
              )}
              priority={index === 0}
            />
          );
        })}
      </div>
    </div>
  );
}
